import React, { useEffect, useId, useMemo, useRef, useState } from "react";

const ChallengeSelect = ({ challenges = [], value, onChange, loading, disabled }) => {
  const inputId = useId();
  const listboxId = useId();
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => challenges.find((c) => String(c.id) === String(value)) || null,
    [challenges, value]
  );

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(selected?.title || "");
  const [activeIndex, setActiveIndex] = useState(-1);

  // Keep text synced if parent changes value
  useEffect(() => {
    setQuery(selected?.title || "");
  }, [selected?.title]);

  // Close on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) {
        setOpen(false);
        setActiveIndex(-1);
        setQuery(selected?.title || "");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [selected?.title]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Optional UX: start filtering only after 2 chars
    // if (q.length < 2) return challenges;
    if (!q) return challenges;
    return challenges.filter((c) => (c.title || "").toLowerCase().includes(q));
  }, [challenges, query]);

  const isDisabled = loading || disabled;

  const selectItem = (item) => {
    onChange(String(item.id));
    setQuery(item.title || "");
    setOpen(false);
    setActiveIndex(-1);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (isDisabled) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((prev) => (prev + 1 >= filtered.length ? 0 : prev + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((prev) => (prev - 1 < 0 ? filtered.length - 1 : prev - 1));
    } else if (e.key === "Enter") {
      if (!open) return;
      e.preventDefault();
      const item = filtered[activeIndex];
      if (item) selectItem(item);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
      setQuery(selected?.title || "");
    }
  };

  return (
    <div ref={wrapRef} style={{ marginBottom: 8, position: "relative" }}>
      {/* ✅ label associated with the input (no eslint a11y issue) */}
      <label htmlFor={inputId} style={{ fontSize: '0.825rem',}}>
        Select a challenge 
     
      <div style={{ marginTop: '1rem' }}>
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          value={query}
          disabled={isDisabled}
          placeholder={loading ? "Loading..." : "Search a challenge..."}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onKeyDown={onKeyDown}
          // ARIA combobox pattern
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            open && activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined
          }
          style={{
            width: "100%",
            padding: "1rem 1.125rem",
            border: "2px solid #e1e5e9",
            borderRadius: 8,
            outline: "none",
            boxSizing:'border-box'
          }}
        />
      </div>
      </label>
      
      {open && !isDisabled && (
        <ul
          id={listboxId}
          role="listbox"
          style={{
            fontFamily: 'VodafoneRg-Regular',
            position: "absolute",
            zIndex: 20,
            width: "100%",
            marginTop: 6,
            maxHeight: 240,
            overflow: "auto",
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: 8,
            padding: 0,
            listStyle: "none",
            boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
          }}
        >
          {filtered.length === 0 ? (
            <li style={{ padding: 10, color: "#777" }}>No results found.</li>
          ) : (
            filtered.map((c, idx) => (
              <li
                key={c.id}
                id={`${listboxId}-opt-${idx}`}
                role="option"
                aria-selected={String(c.id) === String(value)}
                onMouseEnter={() => setActiveIndex(idx)}
                onMouseDown={(e) => e.preventDefault()} // keep focus on input
                onClick={() => selectItem(c)}
                style={{
                  padding: "10px 12px",
                  cursor: "pointer",
                  background: idx === activeIndex ? "#f2f4ff" : "#fff",
                  borderBottom: "1px solid #f2f2f2",
                  fontWeight: String(c.id) === String(value) ? 600 : 400,
                }}
              >
                {c.title}
              </li>
            ))
          )}
        </ul>
      )}

      {!loading && challenges.length === 0 && (
        <div style={{ marginTop: 8, color: "#777" }}>
          No  challenges found.
        </div>
      )}
    </div>
  );
};

export default ChallengeSelect;